import { useState, useEffect, useCallback } from 'react';
import { useUpdateEffect } from 'ahooks';
import { useFetcher } from '@remix-run/react';
import { useNavigate } from '@remix-run/react';

import type { FilterStates } from '@modules/magazine/type/filter-states';
import type { AppliedFilterItem } from '@modules/search/type/filter-state';

import {
    BASE_URL,
    FILTER_API_ENDPOINT,
} from '@modules/magazine/configs/constants';

import {
    getUrlByFilterStates,
    getMagazineKeyByEnum,
} from '@modules/magazine/util/get-magazine-enum-by-key';

import { getAppliedFilters } from '@modules/magazine/util/get-applied-filters';

interface Options {
    initialStates: FilterStates;
    active?: boolean;
}

const clearState: FilterStates = {
    categoryEnum: null,
    year: null,
    month: null,
};

const cloneDeep = (state: FilterStates): FilterStates => {
    return JSON.parse(JSON.stringify(state));
};

export interface UseFilterStatesResult {
    loading: boolean;
    filterStates: FilterStates;
    fetchStates: (state: FilterStates) => void;
    updateFilter: (
        state: Partial<FilterStates>,
        updateStates?: boolean,
    ) => void;
    resetFilters: (state: FilterStates, updateStates?: boolean) => void;
    applyFilterChange: () => void;
    appliedFilters: AppliedFilterItem[];
    clearAllFilters: () => void;
}

const useFilterStates = ({
    initialStates,
    active,
}: Options): UseFilterStatesResult => {
    const [filterStates, setFilterStates] = useState<FilterStates>(
        // we will update the filter states but won't affect the original one
        cloneDeep(initialStates),
    );

    const fetcher = useFetcher<FilterStates>();
    const navigate = useNavigate();

    const fetchStates = useCallback(
        (states: FilterStates) => {
            const { year, month, categoryEnum } = states;
            const key = getMagazineKeyByEnum(categoryEnum);

            const searchParams = new URLSearchParams({
                ...(year && { year: `${year}` }),
                ...(key && { sort: key }),
                ...(month && { month: `${month}` }),
            });
            fetcher.load(
                `${FILTER_API_ENDPOINT}${searchParams && `?${searchParams}`}`,
            );
        },
        [fetcher],
    );

    useUpdateEffect(() => {
        if (!active) return;
        setFilterStates(cloneDeep(initialStates));
    }, [initialStates, active]);

    useEffect(() => {
        if (!fetcher.data) return;
        setFilterStates(fetcher.data);
    }, [fetcher.data]);

    const updateFilter = useCallback(
        (state: Partial<FilterStates>, updateStates = true) => {
            const newStates = {
                ...filterStates,
                ...state,
            };

            setFilterStates(newStates);
            if (updateStates) fetchStates(newStates);
        },
        [filterStates, fetchStates],
    );

    const resetFilters = useCallback(
        (states: FilterStates, apply?: boolean) => {
            setFilterStates(states);
            fetchStates(states);

            if (apply) {
                navigate(BASE_URL, { replace: true });
            }
        },
        [fetchStates, navigate],
    );

    const applyFilterChange = () => {
        navigate(getUrlByFilterStates(filterStates), { replace: true });
    };

    const clearAllFilters = () => {
        resetFilters(clearState, true);
    };

    const appliedFilters = getAppliedFilters(filterStates, resetFilters);

    return {
        loading: fetcher.state === 'loading',
        filterStates,
        fetchStates,
        updateFilter,
        resetFilters,
        applyFilterChange,
        appliedFilters,
        clearAllFilters,
    };
};

export { useFilterStates };
